import React, { useState, useEffect, useMemo } from "react";
import {
  Group,
  Text,
  Stack,
  Button,
  Box,
  ActionIcon,
  Image,
  Skeleton,
  Tooltip,
} from "@mantine/core";
import {
  IconCircleX,
  IconRefresh,
  IconArrowBackUp,
  IconCreditCard,
  IconUserX,
  IconCheck,
  IconCalendarCheck,
  IconShoppingCart,
  IconRosetteDiscount,
  IconDroplet,
  IconPackage,
  IconTrash,
  IconGift,
  IconUserCheck,
} from "@tabler/icons-react";
import moment from "moment";
import {
  GetTransactionsResponse,
  TransactionResponse,
  EditTransactionLineItemsRequest,
  Payment,
} from "../../../../../types/transactions";
import {
  calculateGroupedDiscounts,
  GroupedDiscount,
} from "../../../../../utils/transactionUtils";
import {
  calculateDiscountedItemPrice,
  getDiscountedLinePrice,
  hasLineDiscount,
} from "../../../../../utils/discountUtils";
import {
  computeTransactionTotals,
  refundsForPayment,
  paymentRefundable,
} from "../../../../../utils/transactionTotals";
import { formatDiscountValue } from "../../../../../utils/discountFormatting";
import {
  buildDiscountableLines,
  buildRemoveDiscountGroupRequest,
  DiscountGroup,
} from "../../../../../utils/lineItemDiscounts";
import { DiscountPreset } from "../../../../../types/discounts";
import QuickDiscountsSection from "./QuickDiscountsSection";
import { useApp } from "../../../../../context/AppContext";
import { useSalon } from "../../../../../context/SalonContext";
import { SalonContextType } from "../../../../../types/context";
import { perUnitBreakdown } from "../../../../../utils/quantityUtils";
import { remainingLinePricing } from "../../../../../utils/packageRedemptionUtils";
import { useLoyaltyPreviewEarn } from "../../../../../hooks/useLoyaltyPreviewEarn";
import ChargeCardOnFileModal from "../../../../checkout/ChargeCardOnFileModal";
import RefundPaymentModal from "../../../../checkout/RefundPaymentModal";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import RedemptionBreakdown from "./RedemptionBreakdown";
import { ClientPaymentMethod } from "../../../../../types/cardOnFile";
import TipModal from "./TipModal";
import RebookModal from "./RebookModal/RebookModal";
import { formatPaymentMethod } from "../../../../../utils/paymentMethodFormatters";
import DeleteConfirmationModal from "../../../../common/DeleteConfirmationModal";
import SurfacePaper from "../../../../common/surface/SurfacePaper";
import DashedActionRow from "../../../../common/surface/DashedActionRow";
import surfaceStyles from "../../../../common/surface/surface.module.css";
import { ConnectedCheckoutReminderAlert } from "../../../../common/CheckoutReminderAlert";
import RewardsPromotionsPanel from "./RewardsPromotionsPanel";
import { statusPickBlockedReason } from "../../booking/guards";
import { getSalonTimezone } from "../../../../../utils/timeUtils";
import { LoyaltyRewardPick } from "../../../../rewards/LoyaltyRewardPickerModal";
import styles from "./TransactionSummary.module.css";

interface TransactionSummaryProps {
  transaction: GetTransactionsResponse | TransactionResponse;
  onStatusUpdate: (newStatus: string) => void;
  isUpdatingStatus: boolean;
  onTransactionUpdated: (
    updatedTransaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  onViewNewAppointment?: (transactionId: string) => void;
  /**
   * Rebook in the host's own flow (the calendar's booking drawer / mobile
   * rebook screen). Absent = the legacy RebookModal.
   */
  onRebook?: (
    transaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  canEdit?: boolean;
  /** Opens checkout (owned by TransactionDetailModal). */
  onProceedToCheckout: () => void;
  /** Refetch the transaction (hoisted to TransactionDetailModal). */
  onRefetchTransaction: () => Promise<void>;
  /**
   * Opens the apply-discount modal (rendered by TransactionDetailModal). No
   * args = apply a new discount; "Change" passes the discount group to edit.
   */
  onOpenDiscountModal: (group?: DiscountGroup) => void;
  /**
   * A quick discount's "+ Add": open the apply-discount modal with the preset
   * preselected on every line. Absent = no Quick discounts section.
   */
  onQuickDiscount?: (preset: DiscountPreset) => void;
  /** Persists a discount group's removal via editTransactionLineItems. */
  onDeleteDiscount: (
    request: EditTransactionLineItemsRequest,
  ) => Promise<void> | void;
  /** True while a specific line item's quantity save is in flight — its
   *  server-computed price shows a skeleton until the recalculation lands. */
  isLineItemSaving?: (lineItemId: string) => boolean;
  /** True while any quantity save is in flight — the derived totals
   *  (subtotal / tax / discount / total) skeleton and Checkout is disabled. */
  isRecalculating?: boolean;
  /** Loyalty picks staged from the Rewards & Promotions section — lifted to
   *  TransactionDetailModal so the state survives the Appointment Details
   *  → Checkout screen swap. Absent = feature not wired in this host. */
  stagedRewards?: LoyaltyRewardPick[];
  onStagedRewardsChange?: (picks: LoyaltyRewardPick[]) => void;
}

/** "2 hr", "45 min", "1 hr 30 min" from a minute count. */
function formatDurationLabel(mins: number): string {
  const rounded = Math.max(0, Math.round(mins));
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

export default function TransactionSummary({
  transaction,
  onStatusUpdate,
  isUpdatingStatus,
  onTransactionUpdated,
  onViewNewAppointment,
  onRebook,
  canEdit = true,
  onProceedToCheckout,
  onRefetchTransaction,
  onOpenDiscountModal,
  onQuickDiscount,
  onDeleteDiscount,
  isLineItemSaving = () => false,
  isRecalculating = false,
  stagedRewards,
  onStagedRewardsChange,
}: TransactionSummaryProps) {
  const { editTransactionLineItems, getClientPaymentMethods } = useApp();
  const { services: salonServices, businessInfo } =
    useSalon() as SalonContextType;
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [showChargeCardModal, setShowChargeCardModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState<Payment | null>(null);
  const [showDiscountDeleteConfirmation, setShowDiscountDeleteConfirmation] =
    useState(false);
  const [discountToDelete, setDiscountToDelete] =
    useState<GroupedDiscount | null>(null);
  const [isDeletingDiscount, setIsDeletingDiscount] = useState(false);
  const perms = usePermissions();
  const canRefund = perms.can("payments", "canIssueRefunds");
  const [clientPaymentMethods, setClientPaymentMethods] = useState<
    ClientPaymentMethod[]
  >([]);
  const [hasCardsOnFile, setHasCardsOnFile] = useState(false);

  // Helper function to format amounts - shows "-" for zero values
  const formatAmount = (amount: number): string => {
    return amount === 0 ? "-" : formatCurrency(amount);
  };

  // Placeholder shown in place of a server-recomputed amount while a quantity
  // save is in flight. `w` roughly matches the amount it stands in for so the
  // layout doesn't jump.
  const amountSkeleton = (w = 60, h = 16) => (
    <Skeleton height={h} width={w} radius="sm" display="inline-block" />
  );

  const groupedDiscounts = calculateGroupedDiscounts(transaction);
  const discountableLines = useMemo(
    () => buildDiscountableLines(transaction),
    [transaction],
  );
  const totals = computeTransactionTotals(transaction);

  // Staged loyalty picks — the operator can browse rewards in this modal
  // (RewardsPromotionsPanel) and pick some without committing yet. Those
  // picks live in local state; totalling their dollar impact here lets us
  // (a) render post-redemption Total in the summary AND (b) feed the
  // same dollar amount to the preview-earn hook so the "you'll earn N
  // points" line reflects the netted basis in real time. Without this,
  // the preview only refreshes after the redeem tender actually commits
  // — leaving the number stale mid-pick.
  const stagedRewardDollars = (stagedRewards ?? []).reduce(
    (acc, pick) =>
      acc + pick.quantity * (Number(pick.reward.applied_value) || 0),
    0,
  );

  // Points display strategy differs by transaction phase:
  //   - SETTLED (accrual has fired): show the ACTUAL earned points recorded
  //     on the transaction (`transaction.loyalty_points_earned`, SUM of earn
  //     ledger deltas — backend PR #489). The preview-earn compute uses the
  //     CURRENT basis (post-redemption), which drifts BELOW the accrued number
  //     as soon as any post-settlement redemption reduces the basis; showing
  //     the drift would look like a "shrinking" earned figure that never
  //     actually happened on the ledger.
  //   - NOT SETTLED (accrual has not fired): show the forecast via
  //     preview-earn — the same shared compute accrual will run at settle
  //     time, netted of any staged redemption dollars (matches
  //     CheckoutSummaryPanel exactly; the hook forwards `stagedRewardDollars`
  //     to the backend as `?staged_redemption_dollars=` so the returned
  //     points reflect the post-redemption basis in one round trip).
  // `isLoading` lets the row reserve space with a skeleton while the fetch
  // is in flight so the earned-points text doesn't pop in after the rest of
  // the modal has painted.
  const { preview: earnPreview, isLoading: previewLoading } =
    useLoyaltyPreviewEarn(transaction, stagedRewardDollars);
  const actualPointsEarned = transaction.transaction.loyalty_points_earned;
  const hasActualEarn =
    typeof actualPointsEarned === "number" && actualPointsEarned > 0;
  const expectedPoints = hasActualEarn
    ? actualPointsEarned
    : typeof earnPreview?.expected_points === "number" &&
        earnPreview.expected_points > 0
      ? earnPreview.expected_points
      : 0;
  // Only spin on the preview-earn fetch when we don't already have the
  // committed number; a settled transaction with `loyalty_points_earned` set
  // is authoritative and doesn't need the forecast round-trip.
  const earnLoading = !hasActualEarn && previewLoading;
  const {
    tipsTotal,
    taxBreakdown,
    discountSavings,
    subtotalAfterRedemptions,
    totalAfterRedemptions,
    packageCoverage,
    remainingBalance,
    isNegativeTotal,
  } = totals;

  const isApptPending =
    transaction.transaction.appointment_status === "Requested" ||
    transaction.transaction.appointment_status === "Accepted" ||
    transaction.transaction.appointment_status === "Confirmed" ||
    transaction.transaction.appointment_status === "Arrived";

  // Frontend-only Arrived window (same gate as the status menu).
  const arrivedBlockedReason = statusPickBlockedReason(
    "Arrived",
    transaction,
    getSalonTimezone(businessInfo),
  );

  const isCancelledOrNoShow =
    transaction.transaction.appointment_status === "Cancelled" ||
    transaction.transaction.appointment_status === "No Show";

  const hasBookedServices = transaction.line_items.booked_services.length > 0;

  // Quick sale (incl. package / gift-card sells): no appointment status. It
  // checks out here while money is still owed, or after an auto void (paying
  // un-voids it server-side); a manual void stays void.
  const isQuickSale = transaction.transaction.appointment_status == null;
  const transactionStatus = transaction.transaction.transaction_status;
  const canCheckoutQuickSale =
    isQuickSale &&
    (transactionStatus === "unpaid" ||
      transactionStatus === "partially_paid" ||
      transactionStatus === "void");
  const showProceedToCheckout =
    !isNegativeTotal && (isApptPending || canCheckoutQuickSale);

  const clientId = transaction.client?.id;

  // For Cancelled / No Show appointments we offer a "Charge Card-on-File"
  // action (e.g. a cancellation or no-show fee) — but only when the client
  // actually has a saved card. Look this up once when those statuses apply.
  useEffect(() => {
    if (!canEdit || !isCancelledOrNoShow || !clientId) {
      setHasCardsOnFile(false);
      setClientPaymentMethods([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await getClientPaymentMethods(clientId);
        if (cancelled) return;
        const methods = response.paymentMethods || [];
        setClientPaymentMethods(methods);
        setHasCardsOnFile(methods.length > 0);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load client payment methods:", error);
        setClientPaymentMethods([]);
        setHasCardsOnFile(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canEdit, isCancelledOrNoShow, clientId, getClientPaymentMethods]);

  // Calculate unique staff members
  const getUniqueStaffMembers = () => {
    const uniqueStaff = new Map();
    transaction.line_items.booked_services.forEach((service) => {
      if (service.staff_id && !uniqueStaff.has(service.staff_id)) {
        uniqueStaff.set(service.staff_id, {
          id: service.staff_id,
          name: service.staff_name,
        });
      }
    });
    return Array.from(uniqueStaff.values());
  };

  const handleTipSubmit = async (tipData: EditTransactionLineItemsRequest) => {
    try {
      const bsTipsByIds =
        tipData.line_items.booked_services?.reduce(
          (acc, service) => {
            acc[service.id] = service.tip || 0;
            return acc;
          },
          {} as Record<string, number>,
        ) || {};

      const finalTipData: EditTransactionLineItemsRequest = {
        transaction_id: transaction.transaction.id,
        line_items: {
          booked_services: transaction.line_items.booked_services.map(
            (service) => ({
              id: service.id,
              line_item_id: service.line_item_id,
              tip: bsTipsByIds[service.id] || 0,
            }),
          ),
        },
      };

      const response = await editTransactionLineItems(finalTipData);
      if (response && response.line_items) {
        const updatedTransaction = {
          ...transaction,
          line_items: response.line_items,
        };
        onTransactionUpdated(updatedTransaction);
      }

      setIsTipModalOpen(false);
    } catch (error) {
      console.error("Failed to update tips:", error);
      throw error;
    }
  };

  // Redemptions settle via package coverage, not a payment; never list any
  // legacy package_redemption payment row under Payments Made.
  const succeededPayments = transaction.payments.filter(
    (payment) =>
      payment.status === "succeeded" && payment.type !== "package_redemption",
  );

  // Per Joseph's spec, subtotal + tax stay on FULL amounts and each
  // applied reward posts as its own negative line item right before Total.
  // Total = subtotal(+redemption net) + tax − rewards. `stagedRewardDollars`
  // is computed above the preview-earn hook so both consumers stay in
  // lock-step off one calculation.
  const totalWithRewards = Math.max(
    0,
    totalAfterRedemptions - stagedRewardDollars,
  );
  const remainingWithRewards = Math.max(
    0,
    remainingBalance - stagedRewardDollars,
  );

  const appointmentStatus = transaction.transaction.appointment_status;

  return (
    <>
      <SurfacePaper>
        <Box className={styles.headingRow}>
          <Box className={styles.heading}>Appointment Summary</Box>
        </Box>

        {/* Line items */}
        <Stack gap={14}>
          {transaction.line_items.booked_services.map((service) => {
            const priceBasis = salonServices?.find(
              (svc) => svc.id === service.salon_service_id,
            )?.price_basis;
            // Show only the portion still billed to the client after package
            // redemptions: remaining units at their (discounted) price.
            const { remainingUnits, full, discounted, hasDiscount } =
              remainingLinePricing(service);
            return (
              <Box key={service.id} className={styles.lineItem}>
                <Box className={`${styles.lineItemTile} ${styles.serviceTile}`}>
                  <IconDroplet size={16} />
                </Box>
                <Box className={styles.lineItemBody}>
                  <Box className={styles.lineItemName}>{service.name}</Box>
                  <Box className={styles.lineItemCaption}>
                    {`Service · ${
                      perUnitBreakdown(
                        remainingUnits,
                        priceBasis,
                        service.unit_price,
                      ) ?? formatDurationLabel(service.calc__total_time_mins)
                    }`}
                  </Box>
                  <RedemptionBreakdown
                    service={service}
                    priceBasis={priceBasis}
                    variant="summary"
                  />
                </Box>
                <Box className={styles.lineItemPrice}>
                  {isLineItemSaving(service.line_item_id) ? (
                    amountSkeleton()
                  ) : (
                    <>
                      {hasDiscount && (
                        <Box component="span" className={styles.strikePrice}>
                          {formatAmount(full)}
                        </Box>
                      )}
                      {hasDiscount
                        ? formatAmount(discounted)
                        : formatAmount(full)}
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
          {transaction.line_items.products.map((product) => (
            <Box key={product.id} className={styles.lineItem}>
              <Box className={`${styles.lineItemTile} ${styles.productTile}`}>
                {product.img_url ? (
                  <Image
                    src={product.img_url}
                    w={38}
                    h={38}
                    fit="contain"
                    alt={product.name}
                  />
                ) : (
                  <Box className={styles.productPlaceholder} />
                )}
              </Box>
              <Box className={styles.lineItemBody}>
                <Box className={styles.lineItemName}>{product.name}</Box>
                <Box className={styles.lineItemCaption}>
                  {`Product · ${product.quantity} × ${formatCurrency(
                    product.unit_price,
                  )}`}
                </Box>
              </Box>
              <Box className={styles.lineItemPrice}>
                {isLineItemSaving(product.line_item_id) ? (
                  amountSkeleton()
                ) : (
                  <>
                    {hasLineDiscount(product) && (
                      <Box component="span" className={styles.strikePrice}>
                        {formatAmount(product.unit_price * product.quantity)}
                      </Box>
                    )}
                    {hasLineDiscount(product)
                      ? formatAmount(
                          calculateDiscountedItemPrice(
                            product,
                            product.quantity,
                          ),
                        )
                      : formatAmount(product.unit_price * product.quantity)}
                  </>
                )}
              </Box>
            </Box>
          ))}
          {(transaction.line_items.service_packages ?? []).map((pkg) => {
            // A package's base is its unit_price (the bundle price).
            const hasDiscount = hasLineDiscount(pkg);
            const discountedPrice = hasDiscount
              ? getDiscountedLinePrice(pkg, pkg.unit_price)
              : pkg.unit_price;

            return (
              <Box key={pkg.line_item_id} className={styles.lineItem}>
                <Box className={`${styles.lineItemTile} ${styles.serviceTile}`}>
                  <IconPackage size={16} />
                </Box>
                <Box className={styles.lineItemBody}>
                  <Box className={styles.lineItemName}>{pkg.name}</Box>
                  <Box className={styles.lineItemCaption}>
                    {pkg.services && pkg.services.length > 0
                      ? "Package · granted"
                      : "Package"}
                  </Box>
                </Box>
                <Box className={styles.lineItemPrice}>
                  {isLineItemSaving(pkg.line_item_id) ? (
                    amountSkeleton()
                  ) : (
                    <>
                      {hasDiscount && (
                        <Box component="span" className={styles.strikePrice}>
                          {formatAmount(pkg.unit_price)}
                        </Box>
                      )}
                      {formatAmount(discountedPrice)}
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
          {(transaction.line_items.gift_cards ?? []).map((giftCard) => (
            <Box key={giftCard.line_item_id} className={styles.lineItem}>
              <Box className={`${styles.lineItemTile} ${styles.serviceTile}`}>
                <IconGift size={16} />
              </Box>
              <Box className={styles.lineItemBody}>
                <Box className={styles.lineItemName}>{giftCard.name}</Box>
                <Box className={styles.lineItemCaption}>
                  {giftCard.code ? `Gift card · ${giftCard.code}` : "Gift card"}
                </Box>
              </Box>
              <Box className={styles.lineItemPrice}>
                {formatAmount(giftCard.amount)}
              </Box>
            </Box>
          ))}
        </Stack>

        {/* Rewards & Promotions — feature-gated inside the panel (loyalty
            enabled + client on the transaction + non-zero points balance). */}
        {onStagedRewardsChange && (
          <RewardsPromotionsPanel
            transaction={transaction}
            stagedRewards={stagedRewards ?? []}
            onStagedRewardsChange={onStagedRewardsChange}
          />
        )}

        {/* Discount management — one row per discount group */}
        {groupedDiscounts.length > 0
          ? groupedDiscounts.map((discount) => (
              <Box key={discount.id} className={styles.discountRow}>
                <IconRosetteDiscount
                  size={15}
                  color="var(--color-secondary-active)"
                  style={{ flexShrink: 0 }}
                />
                <Box className={styles.discountName}>
                  {discount.name}{" "}
                  {formatDiscountValue(discount.discount_type, discount.amount)}
                </Box>
                {canEdit && (
                  <>
                    <Button
                      classNames={{ root: styles.changePill }}
                      onClick={() => onOpenDiscountModal(discount)}
                    >
                      Change
                    </Button>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={`Delete discount ${discount.name}`}
                      onClick={() => {
                        setDiscountToDelete(discount);
                        setShowDiscountDeleteConfirmation(true);
                      }}
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </>
                )}
              </Box>
            ))
          : canEdit && (
              <Box mt={20}>
                <DashedActionRow
                  icon={<IconRosetteDiscount size={15} />}
                  label="No discount applied"
                  actionLabel="Add discount"
                  onClick={() => onOpenDiscountModal()}
                >
                  {onQuickDiscount && (
                    <QuickDiscountsSection
                      lines={discountableLines}
                      onAdd={onQuickDiscount}
                    />
                  )}
                </DashedActionRow>
              </Box>
            )}
        {/* Stacking: apply another discount on top of the existing ones. */}
        {canEdit && groupedDiscounts.length > 0 && (
          <Box mt={8}>
            <DashedActionRow
              icon={<IconRosetteDiscount size={15} />}
              label="Add another discount"
              actionLabel="Add discount"
              onClick={() => onOpenDiscountModal()}
            />
          </Box>
        )}

        {/* Totals */}
        <Box className={styles.totals}>
          <Box className={styles.totalsRow}>
            <Text component="span">Subtotal</Text>
            <Text component="span" className={styles.totalsValue}>
              {isRecalculating
                ? amountSkeleton()
                : formatAmount(subtotalAfterRedemptions)}
            </Text>
          </Box>
          {discountSavings > 0 && (
            <Box className={styles.totalsRow}>
              <Text component="span">Discount</Text>
              <Text component="span" className={styles.discountValue}>
                {isRecalculating ? (
                  amountSkeleton()
                ) : (
                  <>−{formatCurrency(discountSavings)}</>
                )}
              </Text>
            </Box>
          )}
          {taxBreakdown.map((tax) => (
            <Box className={styles.totalsRow} key={tax.id}>
              <Text component="span">
                {tax.name} ({(tax.rate * 100).toFixed(3)}%)
              </Text>
              <Text component="span" className={styles.totalsValue}>
                {isRecalculating ? amountSkeleton() : formatAmount(tax.amount)}
              </Text>
            </Box>
          ))}
          {tipsTotal > 0 && (
            <Box className={styles.totalsRow}>
              <Text component="span">
                Tip
                {canEdit && getUniqueStaffMembers().length > 1 && (
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    ml={8}
                    onClick={() => setIsTipModalOpen(true)}
                  >
                    Reallocate Tips
                  </Button>
                )}
              </Text>
              <Text component="span" className={styles.totalsValue}>
                {formatAmount(tipsTotal)}
              </Text>
            </Box>
          )}
          {/* Loyalty picks apply as negative line items just before Total —
              subtotal + tax stay on the full pre-reward amounts (per spec). */}
          {(stagedRewards ?? []).map((pick) => {
            const rewardDollars =
              pick.quantity * (Number(pick.reward.applied_value) || 0);
            const label =
              pick.quantity > 1
                ? `${pick.reward.name} × ${pick.quantity}`
                : pick.reward.name;
            return (
              <Box
                className={styles.totalsRow}
                key={`reward-${pick.reward.reward_id}`}
              >
                <Text component="span">{label}</Text>
                <Text component="span" className={styles.discountValue}>
                  −{formatCurrency(rewardDollars)}
                </Text>
              </Box>
            );
          })}
        </Box>

        <Box className={styles.dashedDivider} />

        <Box className={styles.totalRow}>
          <Box component="span" className={styles.totalLabel}>
            Total
          </Box>
          <Box component="span" className={styles.totalAmount}>
            {isRecalculating
              ? amountSkeleton(96, 24)
              : formatAmount(totalWithRewards)}
          </Box>
        </Box>
        {earnLoading ? (
          <Skeleton
            height={12}
            width={110}
            mt={6}
            ml="auto"
            data-testid="loyalty-expected-points-skeleton"
          />
        ) : (
          expectedPoints > 0 &&
          (() => {
            // Settled (accrual fired): single "N points earned" — ledger
            // truth, no split. Not settled (deposit paid but appointment
            // not complete): the FORECAST (``expectedPoints``) is the
            // TOTAL for the fully-paid appointment. Showing that alone
            // labeled "points earned" reads as "already banked", which
            // it isn't: accrual is all-or-nothing at status='paid'.
            // Split proportionally to what the client has actually paid
            // so far — deposit / upfront charge — so the summary reads
            // "Earned M points" (paid-so-far share) and "Will earn N
            // total points" (full appointment). Single "Will earn"
            // when nothing is paid yet.
            if (hasActualEarn) {
              return (
                <Text
                  size="xs"
                  c="dimmed"
                  ta="right"
                  mt={4}
                  data-testid="loyalty-expected-points"
                >
                  {expectedPoints.toLocaleString("en-US")} points earned
                </Text>
              );
            }
            const paidSoFar = totals.paidAmount;
            const totalForEarn = totals.totalAfterRedemptions;
            const earnedSoFar =
              paidSoFar > 0 && totalForEarn > 0
                ? Math.floor(
                    expectedPoints * Math.min(1, paidSoFar / totalForEarn),
                  )
                : 0;
            return (
              <Stack gap={2} mt={4}>
                {earnedSoFar > 0 && (
                  <Text
                    size="xs"
                    c="dimmed"
                    ta="right"
                    data-testid="loyalty-earned-so-far"
                  >
                    Earned {earnedSoFar.toLocaleString("en-US")} points
                  </Text>
                )}
                <Text
                  size="xs"
                  c="dimmed"
                  ta="right"
                  data-testid="loyalty-expected-points"
                >
                  Will earn {expectedPoints.toLocaleString("en-US")} total
                  points
                </Text>
              </Stack>
            );
          })()
        )}

        {/* Payments Made + Remaining Balance (kept: live functionality the
            mockup omits) */}
        {succeededPayments.length > 0 && (
          <>
            <Box className={styles.hairlineDivider} />
            <Box className={styles.paymentsHeading}>Payments Made</Box>
            <Stack gap="sm">
              {succeededPayments.map((payment) => {
                const paymentRefunds = refundsForPayment(transaction, payment);
                return (
                  <Stack key={payment.id} gap={4}>
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={2}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm">
                            {moment.utc(payment.created_dt).format("M/D/YY")} -{" "}
                            {payment.type} (
                            {formatPaymentMethod(payment.method)})
                          </Text>
                          {canRefund &&
                            paymentRefundable(payment) > 0 &&
                            payment.method !== "Loyalty" &&
                            payment.type !== "loyalty_redemption" && (
                              <Button
                                variant="subtle"
                                size="compact-xs"
                                color="var(--brand-teal)"
                                fz={14}
                                aria-label="Refund payment"
                                onClick={() => {
                                  setPaymentToRefund(payment);
                                  setShowRefundModal(true);
                                }}
                              >
                                Refund
                              </Button>
                            )}
                        </Group>
                        {/* Fallback aggregate for stale/cached responses that
                            carry refunded_amount but not the itemized rows. */}
                        {paymentRefunds.length === 0 &&
                          (payment.refunded_amount || 0) > 0 && (
                            <Text size="xs" c="dimmed">
                              {formatCurrency(payment.refunded_amount)} refunded
                            </Text>
                          )}
                      </Stack>
                      <Text fw={500} c="green">
                        {formatCurrency(payment.amount)}
                      </Text>
                    </Group>

                    {/* Refunds tied to this payment, nested beneath it. */}
                    {paymentRefunds.map((refund) => (
                      <Group
                        key={refund.id}
                        justify="space-between"
                        wrap="nowrap"
                        pl="md"
                      >
                        <Stack gap={0}>
                          <Text size="xs" c="dimmed">
                            {moment.utc(refund.created_dt).format("M/D/YY")}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Refunded via {formatPaymentMethod(refund.method)}
                            {refund.status === "pending" && " (pending)"}
                          </Text>
                          {refund.note && (
                            <Text size="xs" c="dimmed">
                              {refund.note}
                            </Text>
                          )}
                        </Stack>
                        <Text size="xs" fw={500} c="red">
                          ({formatCurrency(refund.amount)})
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}

        {!isNegativeTotal &&
          (isApptPending || isCancelledOrNoShow) &&
          (succeededPayments.length > 0 || packageCoverage > 0) && (
            <>
              <Box className={styles.hairlineDivider} />
              <Group justify="space-between">
                <Text fw={500}>Remaining Balance</Text>
                <Text fw={500} size="lg">
                  {formatAmount(remainingWithRewards)}
                </Text>
              </Group>
            </>
          )}
      </SurfacePaper>

      {/* Salon-configured checkout reminder (Settings → Checkout Reminders),
          read before proceeding — only while this ticket can still check out. */}
      {canEdit && showProceedToCheckout && <ConnectedCheckoutReminderAlert />}

      {/* Action buttons — stacked below the summary paper; sticky-pinned to
          the bottom on mobile. */}
      {canEdit && (
        <Box className={surfaceStyles.actionStack}>
          {showProceedToCheckout && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.primaryAction }}
              leftSection={<IconShoppingCart size={17} />}
              onClick={onProceedToCheckout}
              disabled={isRecalculating}
            >
              Proceed to Checkout
            </Button>
          )}
          {appointmentStatus === "Requested" && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.secondaryAction }}
              leftSection={<IconCheck size={17} />}
              loading={isUpdatingStatus}
              loaderProps={{ color: "var(--brand-teal)" }}
              onClick={() => onStatusUpdate("Accepted")}
            >
              Accept Appointment
            </Button>
          )}
          {appointmentStatus === "Accepted" && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.secondaryAction }}
              leftSection={<IconCalendarCheck size={17} />}
              loading={isUpdatingStatus}
              loaderProps={{ color: "var(--brand-teal)" }}
              onClick={() => onStatusUpdate("Confirmed")}
            >
              Confirm Appointment
            </Button>
          )}
          {(appointmentStatus === "Requested" ||
            appointmentStatus === "Accepted" ||
            appointmentStatus === "Confirmed") && (
            <Tooltip
              label={arrivedBlockedReason}
              disabled={!arrivedBlockedReason}
              withinPortal
            >
              <Box>
                <Button
                  fullWidth
                  classNames={{ root: surfaceStyles.secondaryAction }}
                  leftSection={<IconUserCheck size={17} />}
                  loading={isUpdatingStatus}
                  loaderProps={{ color: "var(--brand-teal)" }}
                  disabled={!!arrivedBlockedReason}
                  onClick={() => onStatusUpdate("Arrived")}
                >
                  Mark as Arrived
                </Button>
              </Box>
            </Tooltip>
          )}
          {appointmentStatus === "No Show" && hasBookedServices && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.softAction }}
              loading={isUpdatingStatus}
              loaderProps={{ color: "var(--brand-teal)" }}
              onClick={() => onStatusUpdate("Confirmed")}
              leftSection={<IconArrowBackUp size={17} />}
            >
              Move back to Confirmed
            </Button>
          )}
          {!isNegativeTotal && isCancelledOrNoShow && hasCardsOnFile && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.secondaryAction }}
              leftSection={<IconCreditCard size={17} />}
              onClick={() => setShowChargeCardModal(true)}
            >
              Charge Card-on-File
            </Button>
          )}
          {hasBookedServices && (
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.secondaryAction }}
              onClick={() =>
                onRebook ? onRebook(transaction) : setShowRebookModal(true)
              }
              leftSection={<IconRefresh size={17} />}
            >
              Rebook
            </Button>
          )}

          {/* Tertiary status actions */}
          {hasBookedServices && isApptPending && (
            <Box className={surfaceStyles.tertiaryRow}>
              {appointmentStatus !== "Requested" && (
                <>
                  <Button
                    classNames={{ root: surfaceStyles.tertiaryButton }}
                    leftSection={<IconUserX size={15} />}
                    loading={isUpdatingStatus}
                    onClick={() => onStatusUpdate("No Show")}
                  >
                    Mark as No Show
                  </Button>
                  <Box className={surfaceStyles.tertiaryDivider} />
                </>
              )}
              <Button
                classNames={{
                  root: `${surfaceStyles.tertiaryButton} ${surfaceStyles.tertiaryDanger}`,
                }}
                leftSection={<IconCircleX size={15} />}
                loading={isUpdatingStatus}
                onClick={() =>
                  onStatusUpdate(
                    appointmentStatus === "Requested"
                      ? "Declined"
                      : "Cancelled",
                  )
                }
              >
                {appointmentStatus === "Requested"
                  ? "Decline Booking"
                  : "Cancel Booking"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Charge Card-on-File Modal (Cancelled / No Show) */}
      {clientId && (
        <ChargeCardOnFileModal
          opened={showChargeCardModal}
          onClose={() => setShowChargeCardModal(false)}
          clientId={clientId}
          transactionId={transaction.transaction.id}
          remainingBalance={Number(remainingBalance.toFixed(2))}
          initialPaymentMethods={clientPaymentMethods}
          onSuccess={async () => {
            await onRefetchTransaction();
            setShowChargeCardModal(false);
          }}
        />
      )}

      {/* Refund Payment Modal */}
      {paymentToRefund && (
        <RefundPaymentModal
          opened={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          payment={paymentToRefund}
          onSuccess={async () => {
            await onRefetchTransaction();
            setShowRefundModal(false);
          }}
        />
      )}

      {/* Tip Modal */}
      <TipModal
        opened={isTipModalOpen}
        onClose={() => setIsTipModalOpen(false)}
        transaction={transaction}
        onSubmit={handleTipSubmit}
      />

      {/* Rebook Modal */}
      <RebookModal
        opened={showRebookModal}
        source="transactionModal"
        onClose={() => setShowRebookModal(false)}
        transaction={transaction}
        onViewNewAppointment={(transactionId) => {
          setShowRebookModal(false);
          if (onViewNewAppointment) {
            onViewNewAppointment(transactionId);
          }
        }}
      />

      {/* Discount Delete Confirmation */}
      <DeleteConfirmationModal
        isOpen={showDiscountDeleteConfirmation}
        onClose={() => setShowDiscountDeleteConfirmation(false)}
        onConfirm={async () => {
          if (!discountToDelete) return;
          try {
            setIsDeletingDiscount(true);
            // Each affected line keeps its other discounts.
            await onDeleteDiscount(
              buildRemoveDiscountGroupRequest(transaction, discountToDelete),
            );
            setShowDiscountDeleteConfirmation(false);
            setIsDeletingDiscount(false);
          } catch (error) {
            console.error("Failed to remove discount:", error);
            setIsDeletingDiscount(false);
          }
        }}
        title="Remove Discount"
        message={`Remove ${discountToDelete?.name ?? "this discount"} from every item on this ticket?`}
        confirmLabel="Remove"
        isLoading={isDeletingDiscount}
      />
    </>
  );
}
